import { useState, useEffect, useMemo, useCallback } from "react";
import Table from "../../common/Table";
import { ChevronDownIcon, ChevronRightIcon, PencilIcon, PlusIcon } from 'lucide-react'
import { GalleryVerticalEnd } from "lucide-react";
import DateTimeCell from "../../common/DateTimeCell";
import { useFetch, useSubmission } from "../../../hooks/useCallApi";
import PublicConsultationRecord from "../modals/PublicConsultationRecord";

function PublicConsultantsTable({
  search,
  startDate,
  endDate,
  header,
  activeTab
}) {
  const [expandedItems, setExpandedItems] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [currentLegislation, setCurrentLegislation] = useState(null);
  const [selectedConsultationId, setSelectedConsultationId] = useState(null);

  console.log("currentLegislation", currentLegislation)

  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [search, startDate, endDate]);

  const params = useMemo(() => ({
    per_page: pagination.perPage,
    page: pagination.currentPage,
    search: search || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    section: "public_consultations",
  }), [pagination, search, startDate, endDate]);

  const { data: publicData, isLoading, refetch } = useFetch(
    ["legislative-documents", params],
    api => api.admin.legislation.getTabView(params)
  );

  const { data: editPublicData, refetch: refetchEditPublic } = useFetch(
    ["edit-consultation-record", selectedConsultationId],
    (api) => api.admin.legislation.getConsultationRecord(selectedConsultationId),
    { enabled: !!selectedConsultationId }
  );

  const tableRows = useMemo(
    () => (Array.isArray(publicData?.data?.data) ? publicData.data.data : []),
    [publicData]
  );

  useEffect(() => {
    if (activeTab === "Public Consultants") {
      refetch();
    }
  }, [activeTab, refetch]);

  const toggleItem = useCallback((id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const handleAddClick = useCallback((consultation) => {
    setCurrentLegislation({
      name: consultation.legislation_number,
      type: consultation.legislation_type,
    });

    setSelectedConsultation(null);
    setSelectedConsultationId(null);
    setModalMode("add");
    setIsModalOpen(true);
  }, []);

  const handleEditClick = useCallback(async (session, consultation) => {
    setCurrentLegislation({
      id: session.consultation_id,
      name: consultation.legislation_number,
      type: consultation.legislation_type,
    });

    setModalMode("edit");
    setIsModalOpen(true);

    setSelectedConsultation(session);

    setSelectedConsultationId(session.consultation_id);
  }, [refetchEditPublic]);


  const handlePageChange = useCallback((page, perPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: perPage || prev.perPage,
    }));
  }, []);

  const capitalizeFirst = (str) => {
    if (!str || typeof str !== "string") return ""
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  const sessionColumns = useMemo(() => ([
    {
      key: "title",
      label: "Title",
      render: (_, session) => (
        <span className="text-sm text-gray-500 truncate max-w-xs  w-full block">
          {session?.title || "N/A"}
        </span>
      ),
    },
    {
      key: "consultation_date",
      label: "Consultation Date",
      render: (_, session) => (
        <DateTimeCell value={session?.consultation_date} showTime={false} />
      ),
    },
    {
      key: "venue",
      label: "Venue",
      render: (_, session) => (
        <span className="truncate text-sm" title={session?.venue}>
          {session?.venue || "N/A"}
        </span>
      ),
    },
    {
      key: "attendee_count",
      label: "No. of Participants",
      render: (_, session) => (
        <span className="text-sm text-gray-500">
          {session?.attendee_count ?? "N/A"}
        </span>
      ),
    },
    {
      key: "minutes_of_meeting",
      label: "Meeting Minutes",
      render: (_, session) => (
        <span className="text-sm text-blue-600 font-medium">
          {session?.public_file?.url ? (
            <a
              href={session.public_file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-blue-800 hover:underline transition-colors duration-200"
            >
              View Minutes
            </a>
          ) : (
            "N/A"
          )}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "Created At",
      render: (_, session) => <DateTimeCell value={session?.created_at} />,
    },
    {
      key: "updated_at",
      label: "Updated At",
      render: (_, session) => <DateTimeCell value={session?.updated_at} />,
    },
  ]), []);

  const columns = useMemo(() => ([
    {
      key: "accordion",
      label: "Public Consultations",
      render: (_, consultation) => {
        const isExpanded = expandedItems[consultation?.legislative_document_id];

        return (
          <div>
            <div
              className="flex items-center cursor-pointer hover:bg-gray-50 transition-colors duration-200 rounded "
              onClick={() => toggleItem(consultation.legislative_document_id)}
            >
              <div className="w-4 h-4 flex items-center justify-center mr-3 text-gray-500">
                {isExpanded ? (
                  <ChevronDownIcon className="w-4 h-4" />
                ) : (
                  <ChevronRightIcon className="w-4 h-4" />
                )}
              </div>

              <div className='flex text-sm items-center gap-10'>
                <h3 className='font-medium text-gray-900 text-sm'>{capitalizeFirst(consultation?.legislation_type)} No. {consultation?.legislation_number}</h3>
                <div className='flex items-center text-gray-500 text-sm gap-2'>
                  <GalleryVerticalEnd className='w-4 h-4' />
                  {consultation?.consultations?.length || 0}
                </div>
              </div>
            </div>

            {isExpanded && (
              <div className="my-4 rounded-lg">
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <Table
                    noContainer
                    columns={[
                      ...sessionColumns,
                      {
                        key: "actions",
                        label: "Actions",
                        render: (_, session) => (
                          <button
                            onClick={() => handleEditClick(session, consultation)}
                            className="text-yellow-600 hover:text-yellow-900 p-1.5 rounded-full hover:bg-yellow-50 transition-all duration-200 hover:scale-110"
                            title="Edit"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>

                        )
                      }
                    ]}
                    data={consultation?.consultations || []}
                    getRowKey={(session) => session.consultation_id}
                  />
                </div>

                <div className="mt-4 flex justify-center">
                  <button
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200"
                    onClick={() => handleAddClick(consultation)}
                  >
                    <PlusIcon className="w-4 h-4" />
                    Upload New Record
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      },
    },
  ]), [expandedItems, toggleItem, sessionColumns]);

  return (
    <div>
      <Table
        header={header}
        hideHeader
        columns={columns}
        data={tableRows}
        getRowKey={row => row.id}
        pagination={{
          currentPage: pagination.currentPage,
          perPage: pagination.perPage,
          lastPage: publicData?.data?.meta?.last_page || 1,
          total: publicData?.data?.meta?.total || 0,
        }}
        onPageChange={handlePageChange}
        onPerPageChange={value =>
          setPagination(prev => ({ ...prev, perPage: value, currentPage: 1 }))
        }
        isLoading={isLoading}
      />

      <PublicConsultationRecord
        isOpen={isModalOpen}
        mode={modalMode}
        data={modalMode === "edit" ? editPublicData?.data : null}
        resolutionName={currentLegislation?.name}
        legislativeId={currentLegislation?.id}
        legislationType={currentLegislation?.type}
        onClose={() => {
          setIsModalOpen(false);
          setCurrentLegislation(null);
          setSelectedConsultation(null);
          setSelectedConsultationId(null);
          setModalMode("add");
        }}
        refetch={refetch}
        refetchEditPublic={refetchEditPublic}
      />

    </div>
  );
}

export default PublicConsultantsTable;
